<template>
  <div>
    <div class="mb-8">
      <h2 class="text-lg font-semibold text-gray-700 mb-1">Monedero</h2>
      <p class="text-sm text-content-400 mb-3">
        Movimiento del saldo por referidos en el periodo. Los reportes de
        ingreso ya registran solo el efectivo cobrado; aquí se ve el pasivo:
        valor bruto vendido = efectivo recibido + monedero redimido.
      </p>

      <div class="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div class="flex flex-col">
          <label class="text-xs text-content-400 mb-1">Desde</label>
          <input type="date" v-model="from" class="border px-3 py-2 rounded" />
        </div>
        <div class="flex flex-col">
          <label class="text-xs text-content-400 mb-1">Hasta</label>
          <input type="date" v-model="to" class="border px-3 py-2 rounded" />
        </div>
        <button @click="loadReport" class="secondary-button">Aplicar</button>
      </div>

      <div v-if="loading" class="flex py-6">
        <loader-icon class="h-8 w-8 text-primary-500 mx-auto"></loader-icon>
      </div>

      <div v-else-if="report">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="bg-white rounded-lg shadow p-4 border-l-4 border-success-500">
            <p class="text-sm text-gray-500 mb-1">Otorgado</p>
            <p class="text-2xl font-bold text-success-500">
              {{ currency(report.granted.total) }}
            </p>
            <p class="text-xs text-content-400 mt-1">
              Referidos: {{ currency(report.granted.referral_rewards) }} ·
              Ajustes: {{ currency(report.granted.admin_adjustments) }}
            </p>
          </div>

          <div class="bg-white rounded-lg shadow p-4 border-l-4 border-primary-500">
            <p class="text-sm text-gray-500 mb-1">Redimido</p>
            <p class="text-2xl font-bold text-primary-500">
              {{ currency(report.redeemed.total) }}
            </p>
            <p class="text-xs text-content-400 mt-1">
              Planes: {{ currency(report.redeemed.plans) }} ·
              Tienda: {{ currency(report.redeemed.shop) }}
            </p>
          </div>

          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Expirado (breakage)</p>
            <p class="text-2xl font-bold text-gray-700">
              {{ currency(report.expired) }}
            </p>
            <p
              v-if="report.negative_adjustments > 0"
              class="text-xs text-content-400 mt-1"
            >
              Ajustes negativos: {{ currency(report.negative_adjustments) }}
            </p>
          </div>

          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Δ pasivo del periodo</p>
            <p
              class="text-2xl font-bold"
              :class="report.net_liability_change >= 0 ? 'text-gray-700' : 'text-success-500'"
            >
              {{ currency(report.net_liability_change) }}
            </p>
            <p class="text-xs text-content-400 mt-1">
              Otorgado − redimido − expirado − ajustes
            </p>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-4 mt-4 border-l-4 border-amber-400">
          <p class="text-sm text-gray-500 mb-1">Pasivo vivo (hoy)</p>
          <p class="text-2xl font-bold text-gray-700">
            {{ currency(report.outstanding.balance) }}
          </p>
          <p class="text-xs text-content-400 mt-1">
            Saldo vigente que se debe a
            {{ report.outstanding.clients }}
            {{ report.outstanding.clients === 1 ? "cliente" : "clientes" }}.
            No depende del rango de fechas.
          </p>
        </div>

        <div class="bg-white rounded-lg shadow overflow-hidden mt-6">
          <div class="px-5 py-3 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Descuentos absorbidos por sucursal
            </h3>
            <p class="text-xs text-content-400 mt-1">
              El cupón de primera compra lo absorbe la sucursal que captó al cliente;
              el monedero, la sucursal donde se canjeó. Ambos ya vienen restados de la
              venta neta de esa sucursal en Resultados y Regalías. La tienda es
              informativa: sus pedidos no entran a la base de regalías.
            </p>
          </div>
          <div v-if="report.absorbed_by_branch && report.absorbed_by_branch.length" class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead class="bg-primary-100">
                <tr>
                  <th class="px-5 py-2 text-left text-xs font-semibold text-primary-700 uppercase tracking-wide">Sucursal</th>
                  <th class="px-4 py-2 text-left text-xs font-semibold text-primary-700 uppercase tracking-wide">Franquicia</th>
                  <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Cupón 1ª compra (planes)</th>
                  <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Monedero (planes)</th>
                  <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Cupón (tienda)</th>
                  <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Monedero (tienda)</th>
                  <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Total absorbido</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in report.absorbed_by_branch"
                  :key="row.branch_id"
                  :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                >
                  <td class="px-5 py-2 font-medium text-gray-800">{{ row.branch_name }}</td>
                  <td class="px-4 py-2 text-gray-600">{{ row.franchise_name || "—" }}</td>
                  <td class="px-4 py-2 text-right text-gray-700">{{ currency(row.plans_coupon) }}</td>
                  <td class="px-4 py-2 text-right text-gray-700">{{ currency(row.plans_wallet) }}</td>
                  <td class="px-4 py-2 text-right text-gray-500">{{ currency(row.shop_coupon) }}</td>
                  <td class="px-4 py-2 text-right text-gray-500">{{ currency(row.shop_wallet) }}</td>
                  <td class="px-4 py-2 text-right font-semibold text-error-500">{{ currency(row.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="px-5 py-4 text-sm text-content-400">
            Ninguna sucursal absorbió cupones ni monedero en este periodo.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import { integerToCurrency } from "@/utils/currencyFormatter";

export default {
  name: "WalletReport",

  data() {
    // Últimos 30 días: mes-en-curso abría el reporte casi vacío los primeros
    // días de cada mes (todo en $0.00 salvo el pasivo vivo).
    const now = new Date();
    const first = new Date(now);
    first.setDate(first.getDate() - 30);
    return {
      from: first.toISOString().slice(0, 10),
      to: now.toISOString().slice(0, 10),
      report: null,
      loading: true,
    };
  },

  created() {
    this.loadReport();
  },

  methods: {
    currency(value) {
      return integerToCurrency(value || 0);
    },

    async loadReport() {
      this.loading = true;
      try {
        this.report = await api.reports.getWalletReport([
          { name: "from", value: this.from },
          { name: "to", value: this.to },
        ]);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.loading = false;
    },
  },
};
</script>
